<script setup>
// The cookie banner + "Cookie settings" dialog (PECR/GDPR):
//  - shows once, before ANY analytics loads — no decision, no trackers
//  - "Essential only" and "Allow analytics" are equal-weight choices
//  - the decision persists a year (cookie + localStorage + HTTP-set cookie)
//    and is changeable any time from the footer's Cookie settings link
// Desktop: a compact card bottom-left. Mobile: a full-width bottom sheet.
import {ref, watch, nextTick, onBeforeUnmount} from 'vue';
import TlDialog from '~/components/TlDialog.vue';
import {useCookieConsent} from '~/composables/useCookieConsent';

const {
  needsDecision, analyticsAllowed, settingsOpen,
  acceptAnalytics, essentialOnly, decide, applyOnLoad,
} = useCookieConsent();

onMounted(() => applyOnLoad());

// Settings dialog holds a draft so Cancel changes nothing.
const draftAnalytics = ref(false);
watch(settingsOpen, (open) => {
  if (open) draftAnalytics.value = analyticsAllowed.value;
});
const saveSettings = () => decide(draftAnalytics.value);

// ---- The banner must not sit on top of anything ----------------------------
// It is position:fixed at the bottom, so on a phone it lands over whatever the
// page happens to end with. On /details-required at 400px that was "Save and
// continue", "Log out" and "Delete it" — every control the screen offers, and,
// for a social sign-in, the terms tick as well. A consent control covered by
// another consent notice is not a cosmetic problem.
//
// The page is given the banner's own height as extra room at the end, so its
// content can always be scrolled clear of it. Measured rather than hardcoded:
// the copy wraps to a different number of lines at different widths. Removed
// again the moment a decision is made, so it costs nothing afterwards.
const bannerEl = ref(null);
let observer = null;

const setReserved = (px) => {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty('--ccb-reserved', px ? `${px}px` : '0px');
  document.body.classList.toggle('has-cookie-banner', px > 0);
};

const measure = () => {
  const el = bannerEl.value;
  if (!el) return setReserved(0);
  // + the gap it floats above on desktop, so the reserve clears the whole thing.
  const gap = window.innerWidth > 600 ? 16 : 0;
  setReserved(Math.ceil(el.getBoundingClientRect().height) + gap);
};

// Watch the ELEMENT, not the flags that decide whether to draw it. The banner
// sits inside <client-only> and a <transition>, so it appears a tick or two
// after needsDecision is already true — a watcher on the flag fires while the
// ref is still null, reserves nothing, and never runs again because the flag
// never changes after that.
watch(bannerEl, async (el) => {
  await nextTick();
  observer?.disconnect();
  observer = null;
  if (!el) return setReserved(0);
  measure();
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(measure);
    observer.observe(el);
  }
}, {immediate: true, flush: 'post'});

onBeforeUnmount(() => {
  observer?.disconnect();
  setReserved(0);
});
</script>

<template>
  <client-only>
    <!-- banner -->
    <transition name="ccb">
      <div v-if="needsDecision && !settingsOpen" ref="bannerEl" class="ccb" role="dialog" aria-label="Cookie consent">
        <div class="ccb-head">
          <v-icon size="18" color="primary">mdi-cookie-outline</v-icon>
          <span>Cookies at Tap Leagues</span>
        </div>
        <p class="ccb-text">
          Essential cookies keep you signed in and payments secure. They're always on.
          With your OK we'd also use <b>Google Analytics</b>, <b>Microsoft Clarity</b> and
          session recording to see what to improve. Analytics only load if you allow them. We always log errors so we can fix them.
          <NuxtLink to="/legal/privacy" class="ccb-link">Privacy policy</NuxtLink>
        </p>
        <div class="ccb-actions">
          <v-btn variant="outlined" class="ccb-btn" @click="essentialOnly">Essential only</v-btn>
          <v-btn color="primary" variant="flat" class="ccb-btn" @click="acceptAnalytics">Allow analytics</v-btn>
        </div>
      </div>
    </transition>

    <!-- settings (footer "Cookie settings") -->
    <tl-dialog v-model="settingsOpen" title="Cookie settings" :max-width="440">
      <div class="ccs-row">
        <div class="ccs-body">
          <div class="ccs-t">Essential</div>
          <div class="ccs-s">Signing in, security (reCAPTCHA) and payment fraud-prevention (Stripe). Always on, as the site can't work without them.</div>
        </div>
        <v-switch :model-value="true" disabled hide-details density="compact" color="primary"/>
      </div>
      <div class="ccs-row">
        <div class="ccs-body">
          <div class="ccs-t">Analytics</div>
          <div class="ccs-s">Google Analytics, Microsoft Clarity and session recording: how the site is used, so we know what to improve. Switching off stops recording and removes their cookies.</div>
        </div>
        <v-switch v-model="draftAnalytics" hide-details density="compact" color="primary"/>
      </div>
      <template #actions>
        <v-btn variant="text" @click="settingsOpen = false">Cancel</v-btn>
        <v-spacer/>
        <v-btn color="primary" variant="flat" @click="saveSettings">Save choices</v-btn>
      </template>
    </tl-dialog>
  </client-only>
</template>

<style scoped lang="scss">
.ccb {
  position: fixed;
  z-index: 3000;
  left: 16px;
  bottom: 16px;
  max-width: 400px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.16);
  padding: 16px;

  @media (max-width: 600px) {
    left: 0;
    right: 0;
    bottom: 0;
    max-width: none;
    border-radius: 16px 16px 0 0;
    border-left: 0;
    border-right: 0;
    border-bottom: 0;
    padding: 16px 16px calc(16px + env(safe-area-inset-bottom));
  }
}

.ccb-head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  font-weight: 700;
}

.ccb-text {
  margin: 8px 0 12px;
  font-size: 12px;
  line-height: 1.6;
  color: rgb(var(--v-theme-textSubtle));
}

.ccb-link {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* the two choices are deliberately the SAME size — rejecting is as easy as accepting */
.ccb-actions {
  display: flex;
  gap: 8px;

  .ccb-btn {
    flex: 1;
    border-radius: 999px;
  }
}

.ccb-enter-active,
.ccb-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.ccb-enter-from,
.ccb-leave-to {
  transform: translateY(16px);
  opacity: 0;
}

.ccs-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;

  & + .ccs-row {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }

  /* the text column yields, the switch keeps its natural size INSIDE the
     dialog — without this the switches hung off the right edge */
  .ccs-body {
    flex: 1 1 auto;
    min-width: 0;
  }

  .v-switch {
    flex: 0 0 auto;

    :deep(.v-selection-control) {
      min-width: 0;
    }
  }
}

.ccs-t {
  font-size: 13px;
  font-weight: 700;
}

.ccs-s {
  margin-top: 2px;
  font-size: 11.5px;
  line-height: 1.5;
  color: rgb(var(--v-theme-textSubtle));
}
</style>

<!-- Unscoped on purpose: the reserve is on <body>, which a scoped block cannot reach. -->
<style lang="scss">
body.has-cookie-banner {
  padding-bottom: var(--ccb-reserved, 0px);
}
</style>
